import {
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
} from '@angular/forms';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { ReCaptcha2Component } from 'ngx-captcha';
import { BehaviorSubject, throwError } from 'rxjs';

import { TerraUtils } from 'src/app/shared/TerraUtils';
import { RegisterRequest } from './register-request.model';
import TermType from '../../shared/enums/TermsTypes.enum';
import { RoutingService } from 'src/app/services/routing.service';
import { UserService } from 'src/app/services/shared/user.service';
import { AppSettingsService } from '../../services/app-settings.service';
import { CustomValidators } from 'src/app/shared/validators/custom.validators';
import { PasswordValidator } from 'src/app/shared/validators/password.validators';
import { AgentDetailsResponse } from '../my-account/model/agentDetailsResponse.model';
import { LoggerService } from '../../shared/errors/logger.service';
import { OrganizationInvitationReadDto } from './OrganizationInvitationReadDto.model';
import { ClientRegistrationState } from './ClientRegistrationState.enum';
import { InvitedUserUpdateDto } from './InvitedUserUpdateDto';
import { AppType } from 'src/app/shared/enums/AppType.enum';
import { AnalyticsServiceNameModel, TelemetryService } from 'telemetry-library';

@Component({
  selector: 'terra-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  showPassword = false;

  registerForm: UntypedFormGroup;

  paths = TerraUtils.consts.paths;
  DEFAULT_ON_BOARDING_URL = this.paths.ONBOARDING;
  DEFAULT_GP_LOGGED_IN_INDEX = this.paths.DASHBOARD;
  EMAIL_VERIFICATION = this.paths.EMAIL_VERIFICATION;
  showCaptchaError = false;

  private DUPLICATE_USER_NAME = 2;
  private CAPTCHA_INVALID = 3;
  showSubmitError = false;
  hasPhoneError = false;
  error: string;
  isLoading = false;
  termType = TermType;
  isLoadingInvitationDetails$ = new BehaviorSubject(false);
  organizationInvitation$ = new BehaviorSubject<OrganizationInvitationReadDto>(
    null
  );
  invitationErrorMessage$ = new BehaviorSubject<string>('');
  invitationToken: string;
  country: number;

  referrerLogo$ = this.userService.gp_referrerLogo$;

  @ViewChild('recaptcha') recaptcha: ReCaptcha2Component;

  constructor(
    private logger: LoggerService,
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    public routingService: RoutingService,
    private telemetryService: TelemetryService,
    public appSettingsService: AppSettingsService
  ) {}

  ngOnInit() {
    this.sendTelemetryEvent();
    // When you reach this page we want to log the user out, so that if he clicks the link to the login page,
    // The alreadyLoggedIn guard on the Login component will not attempt to redirect him to the dashboard (this causes some issues)
    if (this.userService.isLoggedIn()) {
      this.userService.logout().subscribe(() => {
        location.reload(); // Reloading to clear all stored data (clientDetails etc)
      });
    }

    this.titleService.setTitle('Sign up' + TerraUtils.consts.GpPageTitleSuffix);

    this.generatePageForm();
    this.populateFormFromUrl();
    this.getInvitationDetails();
  }

  private getInvitationDetails() {
    this.route.queryParams
      .pipe(
        take(1),
        filter((params) => !!params.invitationToken),
        tap((params) => {
          this.isLoadingInvitationDetails$.next(true);
          this.invitationToken = params.invitationToken;
        }),
        switchMap((_) =>
          this.userService.getOrganizationInvitationInfo(this.invitationToken)
        ),
        finalize(() => this.isLoadingInvitationDetails$.next(false))
      )
      .subscribe(
        (invitation) => {
          if (!invitation) {
            this.invitationErrorMessage$.next(
              'We could not find an invitation with this token.'
            );
            this.logger.error(
              `error getting invitation details for token: ${this.invitationToken}, received empty invitation details from server`
            );
            return;
          } else if (
            invitation.registrationState === ClientRegistrationState.Registered
          ) {
            this.navigateToLogin(invitation.email);
          } else {
            this.organizationInvitation$.next(invitation);
            this.populateForm(invitation);
          }
        },
        (error) => {
          this.invitationErrorMessage$.next(
            'We could not find an invitation with this token.'
          );
          this.logger.error(
            `error getting invitation details for token: ${this.invitationToken}`,
            error
          );
        }
      );
  }

  populateForm(invitation: OrganizationInvitationReadDto) {
    this.registerForm.get('username').setValue(invitation.email);
    this.registerForm.get('username').disable();
  }

  populateFormFromUrl() {
    const email = this.route.snapshot.queryParamMap.get('email');
    if (email) {
      this.registerForm.get('username').setValue(decodeURIComponent(email));
    }
    const firstName = this.route.snapshot.queryParamMap.get('firstName');
    if (firstName) {
      this.registerForm
        .get('firstName')
        .setValue(decodeURIComponent(firstName));
    }
    const lastName = this.route.snapshot.queryParamMap.get('lastName');
    if (lastName) {
      this.registerForm.get('lastName').setValue(decodeURIComponent(lastName));
    }
  }

  generatePageForm() {
    const reqMin2Max30Validators = [
      Validators.required,
      Validators.maxLength(30),
      Validators.minLength(2),
    ];

    this.registerForm = this.fb.group({
      firstName: ['', reqMin2Max30Validators],
      lastName: ['', reqMin2Max30Validators],
      username: ['', [Validators.required, CustomValidators.EmailWithSpaces]],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          PasswordValidator.PasswordStrength,
        ],
      ],
      phone: ['', Validators.required],
      recaptcha: ['', [CustomValidators.requiredIf(() =>
        !this.appSettingsService.isTestingEnv && !this.appSettingsService.isAutomationEnv)]]
    });
  }

  private setupInvitedUser(combinedPhone: string) {
    const data = new InvitedUserUpdateDto();
    data.userName = this.registerForm.get('username').value;
    data.firstName = this.registerForm.get('firstName').value;
    data.lastName = this.registerForm.get('lastName').value;
    data.password = this.registerForm.get('password').value;
    data.phone = combinedPhone;
    data.countryId = this.country;
    data.appType = AppType.GpApp;
    this.userService
      .updateInvitedUserInfo(this.invitationToken, data)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (!response) {
            this.navigateToLogin(
              this.registerForm.get('username').value,
              this.registerForm.get('password').value
            );
          } else {
            const agentDetails = response.clientDetails;
            this.redirectAfterLogin(agentDetails);
          }
        },
        error: (error) => {
          // this.errorMessage$.next('Something went wrong.');
          this.logger.error(
            `error updating invited user details for token: ${this.invitationToken}`,
            error
          );
        },
      });
  }

  addCountryId(country) {
    this.country = country?.id;
  }

  public register() {
    this.showCaptchaError = false;
    this.registerForm.markAllAsTouched();
    this.isLoading = true;
    if (this.registerForm.valid) {
        this.showSubmitError = false;
        const combinedPhone = `${this.registerForm.value.phone.prefix.defaultCountryCallingCode} ${this.registerForm.value.phone.phone}`;

        if (!!this.invitationToken) {
          this.setupInvitedUser(combinedPhone);
          return;
        }

        const login = new RegisterRequest(
          this.registerForm.value.firstName,
          this.registerForm.value.lastName,
          this.registerForm.value.username,
          this.registerForm.value.password,
          combinedPhone,
          this.country
        );
        login.recaptchaToken = this.registerForm.value.recaptcha;
        login.appType = AppType.GpApp;

        this.showPassword = false;

        this.userService
          .register(login)
          .pipe(
            finalize(() => {
              this.isLoading = false;
            }),
            catchError((err) => {
              this.recaptcha && this.recaptcha.resetCaptcha();
              const errResponse = err?.error;
              if (errResponse === this.CAPTCHA_INVALID) {
                this.error = 'Bot verification failed';
              } else if (errResponse === this.DUPLICATE_USER_NAME) {
                this.error =
                  'This email address is already in use, please try a different address. Please contact Covercy’s support for additional assistance at support@covercy.com';
              } else if (errResponse?.modelState) {
                const errState = errResponse?.modelState;
                if (errState['model.SignUp.Password']) {
                  this.error =
                    'Your password should be at least 6 characters long, and contain digits and letters.';
                } else if (errState['model.SignUp.Email']) {
                  this.error =
                    'The email address is invalid. Please check that you have entered it correctly.';
                }
              } else {
                this.error = 'An error has occurred, please contact support.';
              }
              if (
                err.error !== this.DUPLICATE_USER_NAME &&
                err.error !== this.CAPTCHA_INVALID
              ) {
                this.logger.error('error in register GP component, ', err);
              }

              this.showSubmitError = true;
              return throwError(() => err);
            }),
            tap((response) => {
              if (!response) {
                this.navigateToLogin(login.userName, login.password);
              } else {
                const agentDetails = response.clientDetails;
                if (
                  !!agentDetails.email &&
                  !agentDetails.email.includes('@covercy.com')
                ) {
                  const _hsq = ((<any>window)._hsq = (<any>window)._hsq || []);
                  _hsq.push([
                    'identify',
                    {
                      email: agentDetails.email,
                    },
                  ]);
                  _hsq.push(['trackPageView']);
                }
                this.redirectAfterLogin(agentDetails);
              }
            })
          )
          .subscribe();
      } else {
      this.showCaptchaError = true;
      this.isLoading = false;
      this.registerForm.updateValueAndValidity();
      if (this.registerForm.controls.phone.errors != null)
        this.hasPhoneError = true;
    }
  }

  private navigateToLogin(username?: string, password?: string) {
    this.router.navigate([this.routingService.loginGpPage], {
      state: {
        usr: !!username ? username : '',
        pass: !!password ? password : '',
      },
    });
  }

  public redirectAfterLogin(clientDetails: AgentDetailsResponse) {
    if (clientDetails.mustVerifyEmail) {
      // gp & lp - email verification
      this.router.navigate([this.paths.EMAIL_VERIFICATION]);
      return;
    }

    if (!clientDetails.isClientFilledPersonalDetails) {
      this.router.navigate([this.paths.ONBOARDING]);
      return;
    }

    this.routingService.goToReturnUrl(this.DEFAULT_GP_LOGGED_IN_INDEX);
  }

  private sendTelemetryEvent() {
    this.telemetryService.create(
      {
        eventID: '3002',
        eventTitle: 'GP SIGN UP PAGE IMPRESSION',
        sourcePage: document.location.href,
      },
      AnalyticsServiceNameModel.Mixpanel
    );
  }
}
